import React, { useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaTrash } from "react-icons/fa";
import './FormStyles.css'; // Import the CSS file

// Validation schema using Yup
const validationSchema = Yup.object({
  unitPrice: Yup.number()
    .positive("Price must be positive")
    .required("Unit price is required"),
  size: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Size is required'),
  origin: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Origin is required'),
  purchasePrice: Yup.number()
    .positive("Purchase price must be positive")
    .required("Purchase price is required"),
  marketPrice: Yup.number()
    .positive("Market price must be positive")
    .required("Market price is required"),
  sellingPrice: Yup.number()
    .positive("Selling price must be positive")
    .required("Selling price is required"),
  stockQuantity: Yup.number()
    .positive("Quantity must be positive")
    .required("Stock quantity is required"),
  rfidNo: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('RFID number is required'),
  color: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Color is required'),
  weight: Yup.number()
    .positive("Weight must be positive")
    .required("Weight is required"),
  items: Yup.string().matches(/^\S.*$/, 'No leading spaces allowed').required('Rudraksh type is required'),
  photos: Yup.array().min(1, "At least one photo is required/less than 200KB").max(4, "Maximum 4 photos are allowed"),
  number: Yup.string().optional(),
  group: Yup.string().optional(),
  description: Yup.string().optional(),
  altcode: Yup.string().optional(),
});

const apiUrl = 'https://jwapi.infosware-test.in';

const RudrakshForm = ({ handleSave, handleClose, selectedItemType }) => {
  const initialValues = {
    items: "",
    size: "",
    origin: "",
    purchasePrice: "",
    marketPrice: "",
    sellingPrice: "",
    unitPrice: "",
    stockQuantity: "",
    rfidNo: "",
    color: "",
    weight: "",
    number: "",
    group: "",
    description: "",
    altcode: "",
    photos: [],
  };

  const handleSubmit = async (values) => {
    const formData = new FormData();
    formData.append('items', values.items);
    formData.append('type', 'Rudraksh');
    formData.append('size', values.size);
    formData.append('origin', values.origin);
    formData.append('purchasePrice', values.purchasePrice);
    formData.append('marketPrice', values.marketPrice);
    formData.append('sellingPrice', values.sellingPrice);
    formData.append('unitPrice', values.unitPrice);
    formData.append('stockQuantity', values.stockQuantity);
    formData.append('rfidNo', values.rfidNo);
    formData.append('color', values.color);
    formData.append('weight', values.weight);
    formData.append('number', values.number);
    formData.append('group', values.group);
    formData.append('description', values.description);
    formData.append('altcode', values.altcode);
    values.photos.forEach(photo => {
      formData.append('photos', photo);
    });

    try {
      const response = await fetch(`${apiUrl}/api/rudraksh`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        alert("Data saved successfully!");
        handleClose();
      } else {
        alert("Failed to save data.");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      alert("An error occurred. Please try again.");
    }

    handleSave(values);
    handleClose(); // Close the form after saving
  };

  const [tag, setTag] = useState("");

  const fetchTagID = async (setFieldValue) => {
    try {
      const response = await fetch(`${apiUrl}/api/read-tag`); // Correct URL
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setTag(data.tag);
      setFieldValue("rfidNo", data.tag);
    } catch (error) {
      console.error("Error fetching tag ID:", error);
    }
  };

  const handlePhotoChange = (e, setFieldValue, values) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) =>
      ["image/jpeg", "image/png"].includes(file.type) && file.size <= 200 * 1024
    );

    // Ensure values.photos is defined
    if (!values.photos) {
      values.photos = [];
    }

    // Limit to 4 images
    const updatedFiles = validFiles.slice(
      0,
      4 - values.photos.length
    );

    // Store files in form state for preview
    setFieldValue("fileObjects", [
      ...(values.fileObjects || []),
      ...updatedFiles,
    ]);
    setFieldValue("photos", [
      ...values.photos,
      ...updatedFiles,
    ]);
  };

  return (
    <div className="form-container">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, values }) => (
          <FormikForm>
            <div className="form-header">
              <h4 className="mt-3">Add New Rudraksh</h4>
              <Button variant="" className="close-button" onClick={handleClose}>
                &times;
              </Button>
            </div>
            <Row className="mb-3 align-items-center">
              <Col md={6}>
                <Form.Group controlId="formnumber">
                  <Form.Label>Number</Form.Label>
                  <Field type="number" name="number" className="form-control" />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formGroup">
                  <Form.Label>Group</Form.Label>
                  <Field as="select" name="group" className="form-control">
                    <option value="">Select Group</option>
                    <option value="group1">Group 1</option>
                    <option value="group2">Group 2</option>
                    <option value="group3">Group 3</option>
                  </Field>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formDescription">
                  <Form.Label>Description</Form.Label>
                  <Field
                    type="descripton"
                    name="description"
                    className="form-control"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formAltCode">
                  <Form.Label>Alt Code</Form.Label>
                  <Field
                    type="altcode"
                    name="altcode"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="altcode"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formItems">
                  <Form.Label>Rudraksh Type</Form.Label>
                  <Field as="select" name="items" className="form-control" style={{ height: 'auto', overflowY: 'scroll' }}>
                    <option value="">Select type</option>
                    <option value="1 Mukhi">1 Mukhi</option>
                    <option value="2 Mukhi">2 Mukhi</option>
                    <option value="3 Mukhi">3 Mukhi</option>
                    <option value="4 Mukhi">4 Mukhi</option>
                    <option value="5 Mukhi">5 Mukhi</option>
                    <option value="6 Mukhi">6 Mukhi</option>
                    <option value="7 Mukhi">7 Mukhi</option>
                    <option value="8 Mukhi">8 Mukhi</option>
                    <option value="9 Mukhi">9 Mukhi</option>
                    <option value="10 Mukhi">10 Mukhi</option>
                    <option value="11 Mukhi">11 Mukhi</option>
                    <option value="12 Mukhi">12 Mukhi</option>
                    <option value="13 Mukhi">13 Mukhi</option>
                    <option value="14 Mukhi">14 Mukhi</option>
                  </Field>
                  <ErrorMessage
                    name="items"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="formUnitPrice">
                  <Form.Label>Unit Price</Form.Label>
                  <Field
                    type="number"
                    name="unitPrice"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="unitPrice"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formSize">
                  <Form.Label>Size</Form.Label>
                  <Field type="size" name="size" className="form-control" />
                  <ErrorMessage
                    name="size"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formWeight">
                  <Form.Label>Weight</Form.Label>
                  <Field type="weight" name="weight" className="form-control" />
                  <ErrorMessage
                    name="weight"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formColor">
                  <Form.Label>Color</Form.Label>
                  <Field type="text" name="color" className="form-control" />
                  <ErrorMessage
                    name="color"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formStockQuantity">
                  <Form.Label>Stock Quantity</Form.Label>
                  <Field
                    type="stockQuantity"
                    name="stockQuantity"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="stockQuantity"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Purchase Price, Market Price, Selling Price */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formPurchasePrice">
                  <Form.Label>Purchase Price</Form.Label>
                  <Field
                    type="purchasePrice"
                    name="purchasePrice"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="purchasePrice"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formMarketPrice">
                  <Form.Label>Market Price</Form.Label>
                  <Field
                    type="marketPrice"
                    name="marketPrice"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="marketPrice"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Selling Price, RFID Number, and Origin */}
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formSellingPrice">
                  <Form.Label>Selling Price</Form.Label>
                  <Field
                    type="sellingPrice"
                    name="sellingPrice"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="sellingPrice"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formOrigin">
                  <Form.Label>Origin</Form.Label>
                  <Field type="origin" name="origin" className="form-control" />
                  <ErrorMessage
                    name="origin"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group controlId="formPhotos">
                  <Form.Label>Photos</Form.Label>
                  <input
                    type="file"
                    multiple
                    name="photos"
                    accept="image/jpeg, image/png"
                    onChange={(event) => handlePhotoChange(event, setFieldValue, values)}
                    className="form-control"
                  />

                  {/* Show Uploaded Images with Delete Icon */}
                  <div>
                    {values.fileObjects && values.fileObjects.length > 0 && (
                      <div className="image-preview-container">
                        {values.fileObjects.map((file, index) => (
                          <div key={index} className="image-preview-item">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={file.name}
                              style={{
                                width: "90px",
                                height: "40px",
                                marginRight: "10px",
                                marginTop: "10px",
                              }}
                            />
                            <span>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => {
                                // Remove the image from the array
                                const updatedFileObjects =
                                  values.fileObjects.filter((_, i) => i !== index);
                                const updatedPhotos = values.photos.filter(
                                  (_, i) => i !== index
                                );
                                setFieldValue("fileObjects", updatedFileObjects);
                                setFieldValue("photos", updatedPhotos);
                              }}
                              className="btn btn-link text-danger mx-5"
                            >
                              <FaTrash /> {/* Trash icon */}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <ErrorMessage
                    name="photos"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formRfidNo">
                  <Form.Label>RFID Number</Form.Label>
                  <div className="input-group">
                    <Field type="text" name="rfidNo" className="form-control" value={tag} />
                    <Button variant="primary" onClick={() => fetchTagID(setFieldValue)} className="input-group-append">
                      Add
                    </Button>
                  </div>
                  <ErrorMessage
                    name="rfidNo"
                    component="div"
                    className="text-danger"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Submit Button */}
            <Row>
              <Col md={6}>
                <Button variant="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default RudrakshForm;
